import React, { useRef, memo, useMemo } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTabs } from '../../../context/TabsContext';
import { useDocs } from '../../../hooks/useDocs';
import { renderIcon, DefaultCampaignIcon } from '../../SprintDetail/FlowchartView/utils/renderIcon';
import { DocIcon, isDefaultDocIcon } from '../../Sidebar/icons';
import {
  campaignTabCrumbPath,
  formatCampaignPath,
  formatElidedCampaignPath,
  indexUniqueCampaignAncestors,
} from '../../../utils/uniqueCampaignAncestor';
import docsAreaIcon from '../../../assets/area-icons/docs.png';
import dashboardsAreaIcon from '../../../assets/area-icons/dashboards.png';
import campaignsAreaIcon from '../../../assets/area-icons/campaigns.png';
import './TabBar.css';

const isAppIcon = (icon) =>
  icon && typeof icon === 'object' && (icon.type === 'fontawesome' || icon.type === 'custom');

const TAB_ICON_SIZE = 15;

const renderTabIcon = (icon, size = TAB_ICON_SIZE) => {
  return renderIcon(icon, {
    size: `${size}px`,
    fallback: <DefaultCampaignIcon size={size} />,
    defaultColor: '#9ca3af'
  });
};

/** Same mark as Docs view. Default glyph is padded in its viewBox — scale it
 *  so it reads at the same weight as a campaign icon in the same slot. */
const renderTabDocIcon = (icon, size = TAB_ICON_SIZE) => {
  if (isDefaultDocIcon(icon) || icon == null) {
    return <DocIcon size={size} className="tab-bar__tab-doc-mark" />;
  }
  if (isAppIcon(icon)) {
    return renderIcon(icon, { size: `${size}px`, fallback: <DocIcon size={size} /> });
  }
  if (typeof icon === 'object' && icon.type === 'emoji' && icon.value) {
    return <span className="tab-bar__tab-emoji">{icon.value}</span>;
  }
  if (typeof icon === 'string') {
    return <span className="tab-bar__tab-emoji">{icon}</span>;
  }
  return <DocIcon size={size} className="tab-bar__tab-doc-mark" />;
};

/**
 * SortableTab - Individual sortable tab using dnd-kit.
 * When a campaign has an open mission doc (or a cloned campaign needs
 * disambiguation):
 *   [icon, vertically centered]
 *     unique ancestor crumb
 *     doc or campaign name
 */
const SortableTab = memo(({
  tab,
  isActive,
  label,
  icon,
  crumb,
  crumbIcon,
  pathLabel,
  onTabClick,
  onCloseClick,
  onMouseDown,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 100 : undefined,
  };

  const hasCrumb = Boolean(crumb);
  const title = pathLabel && pathLabel !== label
    ? `${pathLabel} — ${label}`
    : hasCrumb
      ? `${crumb} — ${label}`
      : label;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`tab-bar__tab ${isActive ? 'tab-bar__tab--active' : ''} ${isDragging ? 'tab-bar__tab--dragging' : ''} ${hasCrumb ? 'tab-bar__tab--with-doc' : ''}`}
      onClick={(e) => onTabClick(tab, e)}
      onMouseDown={(e) => onMouseDown(e, tab.id)}
      title={title}
      {...attributes}
      {...listeners}
    >
      <span className="tab-bar__tab-icon">{icon}</span>
      <span className="tab-bar__tab-text">
        {hasCrumb && (
          <span className="tab-bar__tab-crumb">
            {crumbIcon && (
              <span className="tab-bar__tab-crumb-icon">{crumbIcon}</span>
            )}
            <span className="tab-bar__tab-crumb-label">{crumb}</span>
          </span>
        )}
        <span className="tab-bar__tab-label">{label}</span>
      </span>
      <button
        className="tab-bar__tab-close"
        onClick={(e) => onCloseClick(e, tab.id)}
        onPointerDown={(e) => e.stopPropagation()} // Prevent drag when clicking close
        aria-label={`Close ${label}`}
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
          <path
            d="M1 1L9 9M9 1L1 9"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </div>
  );
});

SortableTab.displayName = 'SortableTab';

/**
 * TabBar Component
 * 
 * Browser-style tabs displayed in the navbar.
 * Each tab shows the item's icon and name, with a close button.
 * Tabs can be reordered via drag and drop using @dnd-kit.
 *
 * Campaign tabs with an open mission doc stack the shortest unique
 * campaign ancestor as a small breadcrumb above the doc title (and the
 * doc's icon) inside the same chip. Cloned nested campaigns that share a
 * name get the ancestor that actually tells copies apart.
 *
 * When `suppressActive` is set, chips stay visible and clickable, but none
 * is highlighted — the screen isn't a tab.
 */
const TabBar = ({ sprints = [], onTabClick, onNewTab, suppressActive = false }) => {
  const { tabs, activeTabId, closeTab, switchToTab, reorderTabs } = useTabs();
  const { allDocs } = useDocs({ eager: false });
  const tabsContainerRef = useRef(null);

  const docsById = useMemo(() => {
    const map = new Map();
    for (const doc of allDocs) {
      if (doc?.id) map.set(doc.id, doc);
    }
    return map;
  }, [allDocs]);

  const ancestorIndex = useMemo(
    () => indexUniqueCampaignAncestors(sprints),
    [sprints]
  );

  // dnd-kit sensors - pointer with activation distance
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // 5px movement required before drag starts
      },
    })
  );

  // Handle drag end - reorder tabs
  const handleDragEnd = (event) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      const oldIndex = tabs.findIndex(t => t.id === active.id);
      const newIndex = tabs.findIndex(t => t.id === over.id);
      reorderTabs(oldIndex, newIndex);
    }
  };

  // Handle tab click
  const handleTabClick = (tab, e) => {
    // Don't switch if clicking close button
    if (e.target.closest('.tab-bar__tab-close')) return;
    
    switchToTab(tab.id);
    
    // Also trigger the navigation callback
    if (onTabClick) {
      onTabClick(tab);
    }
  };

  // Handle close button click
  const handleCloseClick = (e, tabId) => {
    e.stopPropagation();
    closeTab(tabId);
  };

  // Handle middle-click to close
  const handleMouseDown = (e, tabId) => {
    if (e.button === 1) { // Middle click
      e.preventDefault();
      closeTab(tabId);
    }
  };

  // Get display info for a tab
  const getTabInfo = (tab) => {
    if (tab.type === 'blank') {
      return {
        label: tab.label || 'New Tab',
        icon: <DocIcon size={TAB_ICON_SIZE} className="tab-bar__tab-doc-mark" />,
        crumb: null,
        crumbIcon: null,
      };
    }

    if (tab.type === 'docs') {
      return {
        label: tab.label || 'Documents',
        icon: (
          <img
            className="tab-bar__tab-area-icon"
            src={docsAreaIcon}
            alt=""
            draggable={false}
          />
        ),
        crumb: null,
        crumbIcon: null,
      };
    }

    if (tab.type === 'dashboards') {
      return {
        label: tab.label || 'Dashboards',
        icon: (
          <img
            className="tab-bar__tab-area-icon"
            src={dashboardsAreaIcon}
            alt=""
            draggable={false}
          />
        ),
        crumb: null,
        crumbIcon: null,
      };
    }

    if (tab.type === 'campaigns') {
      return {
        label: tab.label || 'Campaigns',
        icon: (
          <img
            className="tab-bar__tab-area-icon"
            src={campaignsAreaIcon}
            alt=""
            draggable={false}
          />
        ),
        crumb: null,
        crumbIcon: null,
      };
    }

    if (tab.type === 'doc') {
      const doc = docsById.get(tab.targetId);
      return {
        label: tab.label || doc?.title || 'Untitled Doc',
        icon: renderTabDocIcon(doc?.icon || tab.icon),
        crumb: null,
        crumbIcon: null,
      };
    }

    // Try to get fresh data from sprints array
    const sprint = sprints.find(s => s.id === tab.targetId);
    
    // Get the raw icon (could be string or object)
    const rawIcon = sprint?.icon || tab.icon;
    const campaignName = sprint?.name || tab.label || 'Untitled';
    const isContentTab = tab.type === 'campaign' || tab.type === 'template';
    const fullPath = ancestorIndex.pathsById.get(tab.targetId) || [];
    const uniquePath = ancestorIndex.uniqueById.get(tab.targetId) || [];
    const uniqueLabel = formatElidedCampaignPath(uniquePath, fullPath) || campaignName;
    const fullPathLabel = formatCampaignPath(fullPath) || campaignName;
    const crumbIconNode = uniquePath.length
      ? renderTabIcon(uniquePath[uniquePath.length - 1].icon || rawIcon, 10)
      : renderTabIcon(rawIcon, 10);

    // Active doc in this campaign/template tab (mission sheet stack tip)
    if (
      isContentTab &&
      tab.openMissionId &&
      Array.isArray(tab.missionDocStack) &&
      tab.missionDocStack.length > 0
    ) {
      const docId = tab.missionDocStack[tab.missionDocStack.length - 1];
      const doc = docsById.get(docId);
      return {
        label: doc?.title || tab.missionDocTitle || 'Untitled Doc',
        icon: renderTabDocIcon(doc?.icon || tab.missionDocIcon),
        crumb: uniqueLabel,
        crumbIcon: crumbIconNode,
        pathLabel: fullPathLabel,
      };
    }

    if (isContentTab) {
      const campaignCrumbPath = campaignTabCrumbPath(uniquePath, tab.targetId);
      const campaignCrumb = formatElidedCampaignPath(campaignCrumbPath, fullPath);
      if (campaignCrumb) {
        return {
          label: campaignName,
          icon: renderTabIcon(rawIcon),
          crumb: campaignCrumb,
          crumbIcon: renderTabIcon(
            campaignCrumbPath[campaignCrumbPath.length - 1].icon || rawIcon,
            10
          ),
          pathLabel: fullPathLabel,
        };
      }
    }
    
    return {
      label: campaignName,
      icon: renderTabIcon(rawIcon),
      crumb: null,
      crumbIcon: null,
      pathLabel: fullPathLabel,
    };
  };

  if (tabs.length === 0) {
    return null;
  }

  const tabIds = tabs.map(t => t.id);

  return (
    <div className="tab-bar" ref={tabsContainerRef}>
      <div className="tab-bar__tabs">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={tabIds} strategy={horizontalListSortingStrategy}>
            {tabs.map((tab) => {
              const { label, icon, crumb, crumbIcon, pathLabel } = getTabInfo(tab);
              const isActive = !suppressActive && tab.id === activeTabId;

              return (
                <SortableTab
                  key={tab.id}
                  tab={tab}
                  isActive={isActive}
                  label={label}
                  icon={icon}
                  crumb={crumb}
                  crumbIcon={crumbIcon}
                  pathLabel={pathLabel}
                  onTabClick={handleTabClick}
                  onCloseClick={handleCloseClick}
                  onMouseDown={handleMouseDown}
                />
              );
            })}
          </SortableContext>
        </DndContext>
        <button
          type="button"
          className="tab-bar__new-tab"
          onClick={onNewTab}
          title="New tab"
          aria-label="Open new tab"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path
              d="M6 1V11M1 6H11"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      <div className="tab-bar__spacer" aria-hidden />
    </div>
  );
};

export default TabBar;
